import { aaveV4Spokes, parseAaveV4SpokeLenderKey } from '@1delta/data-sdk'
import { AdditionalYields } from '../../../types'
import { GenericTokenList } from '../../../types/general'
import { createMarketUid } from '../../../utils'
import { lenderShortName } from '../../../utils/marketName'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import {
  BASE_CALLS_PER_RESERVE,
  CALLS_PER_SPOKE_TAIL,
  MAX_HUB_ASSETS,
  getAaveV4ExpectedCallCount,
} from './publicCallBuild'
import type {
  ParsedSpokeData,
  ParsedReserveData,
  SpokeReserve,
  SpokeReserveConfig,
  DynamicReserveConfig,
  LiquidationConfig,
  HubAsset,
  SpokeBinding,
} from './fetcher/types'
import {
  normalizeAaveV4,
  NormalizedAaveV4Response,
} from './fetcher/normalize'

/** Helper: is address valid (not empty, not "0x") */
function isValidAddress(addr: string | undefined): boolean {
  return !!addr && addr !== '0x' && addr.length > 2
}

/**
 * Parses the multicall result for ONE Aave V4 spoke. The lender key encodes
 * the spoke address (`AAVE_V4_<HEX>`); we resolve the spoke entry from
 * `aaveV4Spokes()` and walk the same call layout produced by
 * `buildAaveV4LenderReserveCall`.
 *
 * Hub asset enumeration runs once per **distinct hub** referenced by the
 * spoke's reserves (the spoke can mix hubs). Reserves are then matched to
 * their hub asset via their own `reserve.hub` field, not via a single
 * spoke-level hub.
 */
export const getAaveV4ReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [
  (data: any[]) => NormalizedAaveV4Response | undefined,
  number,
] => {
  const expectedCalls = getAaveV4ExpectedCallCount(chainId, lender)

  const converter = (
    data: any[],
  ): NormalizedAaveV4Response | undefined => {
    if (data.length !== expectedCalls) return undefined

    const parsed = parseAaveV4SpokeLenderKey(lender)
    if (!parsed) return undefined

    const spokeEntry = aaveV4Spokes()?.[chainId]?.[parsed.spokeAddrLower]
    if (!spokeEntry) return undefined

    const spokeAddr = spokeEntry.spoke.toLowerCase()
    const reserves = spokeEntry.reserves ?? []
    const dynConfigCount = (spokeEntry.dynamicConfigKeyMax ?? 0) + 1

    let offset = 0
    const parsedReserves: ParsedReserveData[] = []

    // ── Phase 1: per-reserve base + dynamic config calls ───────────────
    for (const r of reserves) {
      const reserveId = r.reserveId

      const rawReserve = data[offset]
      const rawConfig = data[offset + 1]
      const rawSupplied = data[offset + 2]
      const rawDebt = data[offset + 3]
      const rawTotalDebt = data[offset + 4]
      offset += BASE_CALLS_PER_RESERVE

      const dynamicConfigs: { [configKey: number]: DynamicReserveConfig } = {}
      for (let key = 0; key < dynConfigCount; key++) {
        const rawDynConfig = data[offset]
        offset += 1
        if (rawDynConfig && rawDynConfig !== '0x') {
          dynamicConfigs[key] = {
            collateralFactor: Number(rawDynConfig?.collateralFactor ?? 0),
            maxLiquidationBonus: Number(
              rawDynConfig?.maxLiquidationBonus ?? 0,
            ),
            liquidationFee: Number(rawDynConfig?.liquidationFee ?? 0),
          }
        }
      }

      // The on-chain `getReserve` response carries `hub` directly. Prefer
      // that as the canonical hub for this reserve; fall back to the
      // metadata `r.hub` (which the data-sdk adapter stamps from the
      // legacy fork-keyed map).
      const onChainHub = (rawReserve?.hub ?? '').toLowerCase()
      const reserveHub = isValidAddress(onChainHub)
        ? onChainHub
        : (r.hub ?? '').toLowerCase()

      const reserve: SpokeReserve = {
        underlying: rawReserve?.underlying ?? r.underlying ?? '',
        hub: reserveHub,
        assetId: Number(rawReserve?.assetId ?? r.assetId ?? 0),
        decimals: Number(rawReserve?.decimals ?? 18),
        collateralRisk: Number(rawReserve?.collateralRisk ?? 0),
        flags: Number(rawReserve?.flags ?? 0),
        dynamicConfigKey: Number(rawReserve?.dynamicConfigKey ?? 0),
      }

      const config: SpokeReserveConfig = {
        collateralRisk: Number(rawConfig?.collateralRisk ?? 0),
        paused: rawConfig?.paused ?? false,
        frozen: rawConfig?.frozen ?? false,
        borrowable: rawConfig?.borrowable ?? false,
        receiveSharesEnabled: rawConfig?.receiveSharesEnabled ?? false,
      }

      let drawnDebt = 0n
      let premiumDebt = 0n
      if (Array.isArray(rawDebt)) {
        drawnDebt = BigInt(rawDebt[0] ?? 0)
        premiumDebt = BigInt(rawDebt[1] ?? 0)
      } else {
        drawnDebt = BigInt(rawDebt ?? 0)
      }

      parsedReserves.push({
        reserveId,
        reserve,
        config,
        dynamicConfigs,
        suppliedAssets: BigInt(rawSupplied ?? 0),
        drawnDebt,
        premiumDebt,
        totalDebt: BigInt(rawTotalDebt ?? 0),
        hubAsset: { drawnRate: 0n } as HubAsset,
        price: 0n,
      })
    }

    // ── Phase 2: spoke-level liquidation config ────────────────────────
    const rawLiqConfig = data[offset]
    offset += CALLS_PER_SPOKE_TAIL

    const liquidationConfig: LiquidationConfig = {
      targetHealthFactor: BigInt(rawLiqConfig?.targetHealthFactor ?? 0),
      healthFactorForMaxBonus: BigInt(
        rawLiqConfig?.healthFactorForMaxBonus ?? 0,
      ),
      liquidationBonusFactor: Number(
        rawLiqConfig?.liquidationBonusFactor ?? 0,
      ),
    }

    const spokeData: ParsedSpokeData = {
      spoke: spokeAddr,
      oracle: spokeEntry.oracle,
      label: spokeEntry.label,
      oracleDecimals: 8,
      liquidationConfig,
      reserves: parsedReserves,
    }

    // ── Phase 3: oracle prices batch (one call covering all reserves) ─
    if (parsedReserves.length > 0 && isValidAddress(spokeEntry.oracle)) {
      const rawPrices = data[offset]
      offset += 1
      const priceArray = Array.isArray(rawPrices) ? rawPrices : []
      for (let ri = 0; ri < parsedReserves.length; ri++) {
        if (ri < priceArray.length) {
          parsedReserves[ri].price = BigInt(priceArray[ri] ?? 0)
        }
      }
    }

    // ── Phase 4: per-distinct-hub asset enumeration + spoke bindings ──
    // Iteration order MUST match `buildAaveV4LenderReserveCall`. Build the
    // distinct-hub list from the metadata reserve order (NOT from parsed
    // reserves, because the parser's `reserve.hub` may have been replaced
    // with a different on-chain value while the call layout used the
    // metadata value).
    const distinctHubs: string[] = []
    {
      const seen = new Set<string>()
      for (const r of reserves) {
        const h = (r?.hub ?? '').toLowerCase()
        if (!h || !isValidAddress(h)) continue
        if (seen.has(h)) continue
        seen.add(h)
        distinctHubs.push(h)
      }
    }

    // Per-hub asset map: hubAddrLower → underlyingLower → HubAsset
    const hubAssetMap = new Map<string, Map<string, HubAsset>>()

    for (const hub of distinctHubs) {
      const rawAssetCount = data[offset]
      offset += 1

      const assetCount = Math.min(
        Number(rawAssetCount ?? 0),
        MAX_HUB_ASSETS,
      )

      const inner = new Map<string, HubAsset>()
      hubAssetMap.set(hub, inner)

      for (let i = 0; i < MAX_HUB_ASSETS; i++) {
        const baseIdx = offset + i * 3
        const rawHubAsset = data[baseIdx]
        const rawAddedAssets = data[baseIdx + 1]
        const rawTotalOwed = data[baseIdx + 2]

        if (i >= assetCount || !rawHubAsset || rawHubAsset === '0x')
          continue

        const underlying = (rawHubAsset?.underlying ?? '').toLowerCase()
        if (!underlying) continue

        inner.set(underlying, {
          liquidity: BigInt(rawHubAsset?.liquidity ?? 0),
          realizedFees: BigInt(rawHubAsset?.realizedFees ?? 0),
          decimals: Number(rawHubAsset?.decimals ?? 18),
          addedShares: BigInt(rawHubAsset?.addedShares ?? 0),
          swept: BigInt(rawHubAsset?.swept ?? 0),
          premiumOffsetRay: BigInt(rawHubAsset?.premiumOffsetRay ?? 0),
          drawnShares: BigInt(rawHubAsset?.drawnShares ?? 0),
          premiumShares: BigInt(rawHubAsset?.premiumShares ?? 0),
          liquidityFee: Number(rawHubAsset?.liquidityFee ?? 0),
          drawnIndex: BigInt(rawHubAsset?.drawnIndex ?? 0),
          drawnRate: BigInt(rawHubAsset?.drawnRate ?? 0),
          lastUpdateTimestamp: Number(
            rawHubAsset?.lastUpdateTimestamp ?? 0,
          ),
          underlying: rawHubAsset?.underlying ?? '',
          irStrategy: rawHubAsset?.irStrategy ?? '',
          reinvestmentController: rawHubAsset?.reinvestmentController ?? '',
          feeReceiver: rawHubAsset?.feeReceiver ?? '',
          deficitRay: BigInt(rawHubAsset?.deficitRay ?? 0),
          totalAddedAssets: BigInt(rawAddedAssets ?? 0),
          totalOwedAssets: BigInt(rawTotalOwed ?? 0),
        })
      }

      offset += MAX_HUB_ASSETS * 3

      // Spoke bindings for every reserve attached to this hub. Match
      // metadata iteration order — same as the call builder.
      for (const r of reserves) {
        if ((r?.hub ?? '').toLowerCase() !== hub) continue
        if (typeof r.assetId !== 'number') continue

        const rawBinding = data[offset]
        offset += 1
        if (!rawBinding || rawBinding === '0x') continue

        const binding: SpokeBinding = {
          riskPremiumThreshold: Number(
            rawBinding?.riskPremiumThreshold ?? 0,
          ),
          active: Boolean(rawBinding?.active ?? false),
          halted: Boolean(rawBinding?.halted ?? false),
          addCap: Number(rawBinding?.addCap ?? 0),
          drawCap: Number(rawBinding?.drawCap ?? 0),
          deficitRay: BigInt(rawBinding?.deficitRay ?? 0),
        }

        const target = parsedReserves.find(
          (pr) => pr.reserveId === r.reserveId,
        )
        if (target) target.spokeBinding = binding
      }
    }

    // ── Phase 5: assign hub asset to each reserve via its own hub ─────
    for (const pr of parsedReserves) {
      const hub = pr.reserve.hub?.toLowerCase?.() ?? ''
      const underlying = pr.reserve.underlying?.toLowerCase?.() ?? ''
      if (!hub || !underlying) continue
      const hubAsset = hubAssetMap.get(hub)?.get(underlying)
      if (hubAsset) pr.hubAsset = hubAsset
    }

    return normalizeAaveV4(
      [spokeData],
      chainId,
      lender,
      prices,
      additionalYields,
      tokenList,
      lenderShortName,
      createMarketUid,
      toOracleKey,
      toGenericPriceKey,
    )
  }

  return [converter, expectedCalls]
}
